/**
 * Cascading Country → State → City from the same master APIs as `useLocationMaster`,
 * with isolated state so two pickers (e.g. origin + destination) do not overwrite each other.
 */
import { useCallback, useEffect, useState } from 'react';
import CommonService from '../services/common.service';

export function useLocationPicker() {
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const response = await CommonService.get('/master/company-Country');
        if (cancelled) return;
        if (response && response.success && response.data) {
          const apiCountries = Array.isArray(response.data)
            ? response.data
            : response.data.countries || response.data.master_countries || [];
          const mappedCountries = apiCountries
            .map((country) => {
              const id = country.country_id ?? country.id ?? country.value;
              const label =
                country.country_name ?? country.country_label ?? country.label ?? country.name ?? country.country;
              return { id, label };
            })
            .filter((c) => c.id !== undefined && c.id !== null && c.label);
          setCountries(mappedCountries);
        }
      } catch (error) {
        console.error('Error fetching countries from API:', error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const loadStates = useCallback(async (countryId) => {
    if (!countryId) {
      setStates([]);
      setCities([]);
      return;
    }
    try {
      setLoadingStates(true);
      const response = await CommonService.get(`/master/company-State/${countryId}`);
      if (response && response.success && response.data) {
        const apiStates = Array.isArray(response.data)
          ? response.data
          : response.data.states || response.data.master_states || [];
        const mappedStates = apiStates
          .map((state) => {
            const id = state.state_id ?? state.id ?? state.value;
            const label = state.state_name ?? state.state_label ?? state.label ?? state.name ?? state.state;
            return { id, label };
          })
          .filter((s) => s.id !== undefined && s.id !== null && s.label);
        setStates(mappedStates);
        setCities([]);
      }
    } catch (error) {
      console.error('Error fetching states from API:', error);
      setStates([]);
    } finally {
      setLoadingStates(false);
    }
  }, []);

  const loadCities = useCallback(async (stateId) => {
    if (!stateId) {
      setCities([]);
      return;
    }
    try {
      setLoadingCities(true);
      const response = await CommonService.get(`/master/company-City/${stateId}`);
      if (response && response.success && response.data) {
        const apiCities = Array.isArray(response.data)
          ? response.data
          : response.data.cities || response.data.master_cities || [];
        const mappedCities = apiCities
          .map((city) => {
            const id = city.city_id ?? city.id ?? city.value;
            const label = city.city_name ?? city.city_label ?? city.label ?? city.name ?? city.city;
            return { id, label };
          })
          .filter((c) => c.id !== undefined && c.id !== null && c.label);
        setCities(mappedCities);
      }
    } catch (error) {
      console.error('Error fetching cities from API:', error);
      setCities([]);
    } finally {
      setLoadingCities(false);
    }
  }, []);

  return {
    countries,
    states,
    cities,
    loading,
    loadingStates,
    loadingCities,
    loadStates,
    loadCities,
  };
}
